import { useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { useUpdateCallActionTopic, type CalendarCallAction } from "@/hooks/use-calendar";

/** Shared by compact and full callback rows, including automatic missed calls. */
export function CallbackRequestTopic({
  action,
  userId,
  compact,
}: {
  action: CalendarCallAction;
  userId?: number;
  compact?: boolean;
}) {
  const updateTopic = useUpdateCallActionTopic();
  const [editor, setEditor] = useState<{ draft: string; expectedTopic: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canEdit = userId != null && action.requesterUserId === userId
    && (action.status === "pending" || action.status === "requested");
  const tooLong = (editor?.draft.trim().length ?? 0) > 200;

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!editor || !canEdit || tooLong || updateTopic.isPending) return;
    setError(null);
    try {
      await updateTopic.mutateAsync({
        id: action.id,
        topic: editor.draft.trim() || null,
        expectedTopic: editor.expectedTopic,
      });
      setEditor(null);
    } catch (failure) {
      const status = failure && typeof failure === "object" && "status" in failure ? failure.status : null;
      setError(status === 409
        ? "This request changed while you were editing. Your draft is kept. Cancel and reopen to review the latest topic."
        : failure instanceof Error ? failure.message : "Could not save topic. Please try again.");
    }
  };

  if (editor && canEdit) {
    return (
      <form onSubmit={save} className="mt-1 space-y-1">
        <textarea
          aria-label="Callback topic"
          aria-describedby={tooLong || error ? `callback-topic-error-${action.id}` : undefined}
          aria-invalid={tooLong || !!error}
          autoFocus
          rows={2}
          className="w-full min-w-0 rounded border border-gray-600 bg-gray-800 px-2 py-1 text-xs text-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
          value={editor.draft}
          disabled={updateTopic.isPending}
          onChange={(event) => setEditor({ ...editor, draft: event.target.value })}
        />
        {(tooLong || error) && (
          <div id={`callback-topic-error-${action.id}`} role="alert" className="text-[10px] text-red-300">
            {tooLong ? "Topic must be 200 characters or fewer." : error}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-1">
          <Button type="submit" size="sm" className="h-6 px-2 text-[10px]" disabled={tooLong || updateTopic.isPending}>
            {updateTopic.isPending ? "Saving…" : "Save"}
          </Button>
          <Button
            type="button" size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] text-gray-300"
            disabled={updateTopic.isPending}
            onClick={() => { setEditor(null); setError(null); }}
          >
            Cancel
          </Button>
          <span className="ml-auto text-[10px] text-gray-400">{editor.draft.trim().length}/200</span>
        </div>
      </form>
    );
  }

  return (
    <>
      {action.topic && (
        <div
          className={compact ? "text-xs text-amber-100 truncate" : "text-sm font-medium text-amber-100 leading-snug break-words mt-0.5"}
          data-testid={`calendar-request-topic-${action.id}`}
        >
          {action.topic}
        </div>
      )}
      {canEdit && (
        <Button
          type="button" size="sm" variant="ghost" className="h-5 px-1 text-[10px] text-amber-300"
          onClick={() => {
            // Capture once at open; background refreshes must not replace the
            // user's draft or move the optimistic-concurrency baseline.
            setEditor({ draft: action.topic ?? "", expectedTopic: action.topic ?? null });
            setError(null);
            updateTopic.reset();
          }}
        >
          {action.topic ? "Edit topic" : "Add topic"}
        </Button>
      )}
    </>
  );
}
